import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { getSyncState, subscribe as subscribeSync, startSync } from '../utils/contactsSyncStore';
import '../styles/ClientAccessPage.css';

const SOURCE_LABELS = {
  google: { label: 'Google', icon: '🔵' },
  apple: { label: 'iCloud', icon: '🍎' },
  vcf: { label: 'VCF', icon: '📂' },
  csv: { label: 'CSV', icon: '📊' },
  manual: { label: null, icon: null },
};

// Labels used for the source filter chips (manual gets a visible label here)
const SOURCE_FILTER_LABELS = {
  google: '🔵 Google',
  apple: '🍎 iCloud',
  vcf: '📂 VCF',
  csv: '📊 CSV',
  manual: '✍️ يدوي',
};

const PAGE_SIZE = 50;

export default function ClientAccessPage() {
  const [privateMode, setPrivateMode] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Contacts sync state
  const [syncStatus, setSyncStatus] = useState({ google: null, apple: null });
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [syncingApple, setSyncingApple] = useState(false);
  const [showAppleModal, setShowAppleModal] = useState(false);
  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [connectingApple, setConnectingApple] = useState(false);
  const [firstSyncProvider, setFirstSyncProvider] = useState(null); // 'google' during initial import

  // Selection / bulk / inline-edit state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Disconnect modal: { provider: 'google'|'apple', count: number } | null
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    loadData();
    loadSyncStatus();
    // Handle redirect back from Google OAuth
    const params = new URLSearchParams(window.location.search);
    const syncParam = params.get('sync');
    if (syncParam === 'google_success') {
      window.history.replaceState({}, '', window.location.pathname);
      runFirstGoogleSync();
    } else if (syncParam === 'google_error') {
      const msg = params.get('msg') || 'فشل ربط Google Contacts';
      toast.error(msg);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter]);

  // If the selected source filter no longer has any contacts (e.g. all were
  // deleted or disconnected), fall back to "All sources".
  useEffect(() => {
    if (sourceFilter !== 'all' && !clients.some((c) => (c.source || 'manual') === sourceFilter)) {
      setSourceFilter('all');
    }
  }, [clients, sourceFilter]);

  // Reflect the shared (cross-page) sync state. A sync started here keeps
  // running in the background; if we come back mid-sync the button stays
  // disabled and the "syncing" message shows. On completion we refresh.
  useEffect(() => {
    const initial = getSyncState();
    setSyncingGoogle(initial.google);
    setSyncingApple(initial.apple);
    const unsub = subscribeSync((st, event) => {
      setSyncingGoogle(st.google);
      setSyncingApple(st.apple);
      if (event?.done) {
        if (event.ok && event.data?.provider) {
          setSyncStatus((prev) => ({ ...prev, [event.provider]: event.data.provider }));
        }
        loadData();
      }
    });
    return unsub;
  }, []); // eslint-disable-line

  async function runFirstGoogleSync() {
    toast.success('تم ربط Google Contacts بنجاح!');
    await loadSyncStatus();
    setFirstSyncProvider('google');
    try {
      const { data } = await api.post('/backoffice/contacts-sync/google/sync');
      if (data.success) {
        toast.success(`تم استيراد ${data.count} جهة اتصال من Google`);
        setSyncStatus((s) => ({ ...s, google: data.provider }));
      } else {
        toast.error(data.message || 'تعذّر استيراد جهات الاتصال');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في استيراد جهات الاتصال');
    }
    await loadData();
    setFirstSyncProvider(null);
  }

  async function loadData() {
    try {
      const [modeRes, clientsRes] = await Promise.all([
        api.get('/backoffice/private-mode'),
        api.get('/backoffice/allowed-clients'),
      ]);
      if (modeRes.data.success) setPrivateMode(modeRes.data.private_mode);
      if (clientsRes.data.success) setClients(clientsRes.data.clients || []);
    } catch {
      toast.error('خطأ في تحميل البيانات');
    }
    setLoading(false);
  }

  async function loadSyncStatus() {
    try {
      const { data } = await api.get('/backoffice/contacts-sync');
      if (data.success) setSyncStatus({ google: data.google, apple: data.apple });
    } catch {
      /* silently ignore — sync is optional */
    }
  }

  async function togglePrivateMode() {
    try {
      const { data } = await api.put('/backoffice/private-mode', { enabled: !privateMode });
      if (data.success) {
        setPrivateMode(data.private_mode);
        toast.success(data.private_mode ? 'تم تفعيل الوضع الخاص' : 'تم إلغاء الوضع الخاص');
      }
    } catch {
      toast.error('خطأ في تحديث الإعدادات');
    }
  }

  async function addClient(e) {
    e.preventDefault();
    if (!newPhone.trim()) return;
    try {
      const { data } = await api.post('/backoffice/allowed-clients', {
        phone: newPhone.trim(),
        name: newName.trim() || null,
      });
      if (data.success) {
        setClients([data.client, ...clients]);
        setNewPhone('');
        setNewName('');
        setShowAddForm(false);
        toast.success('تمت إضافة العميل');
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الإضافة');
    }
  }

  async function blockClient(id) {
    try {
      const { data } = await api.put(`/backoffice/allowed-clients/${id}/block`);
      if (data.success) {
        setClients(clients.map((c) => (c.id === id ? data.client : c)));
        toast.success('تم حظر العميل');
      }
    } catch {
      toast.error('خطأ في الحظر');
    }
  }

  async function unblockClient(id) {
    try {
      const { data } = await api.put(`/backoffice/allowed-clients/${id}/unblock`);
      if (data.success) {
        setClients(clients.map((c) => (c.id === id ? data.client : c)));
        toast.success('تم إلغاء الحظر');
      }
    } catch {
      toast.error('خطأ في إلغاء الحظر');
    }
  }

  async function deleteClient(id) {
    const client = clients.find((c) => c.id === id);
    if (client && (client.source === 'google' || client.source === 'apple')) {
      const providerName = client.source === 'google' ? 'Google Contacts' : 'iCloud';
      if (!window.confirm(`هذا العميل مرتبط بـ ${providerName}. سيتم إضافته مرة أخرى عند المزامنة التالية ما لم تحذفه من ${providerName}. هل تريد الحذف المؤقت؟`)) return;
    } else {
      if (!window.confirm('هل تريد حذف هذا العميل من القائمة؟')) return;
    }
    try {
      const { data } = await api.delete(`/backoffice/allowed-clients/${id}`);
      if (data.success) {
        setClients(clients.filter((c) => c.id !== id));
        toast.success('تم حذف العميل');
      }
    } catch {
      toast.error('خطأ في الحذف');
    }
  }

  async function handleFileImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['vcf', 'csv'].includes(ext)) {
      toast.error('نوع الملف غير مدعوم. استخدم VCF أو CSV');
      return;
    }
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/backoffice/allowed-clients/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        const parts = [];
        if (data.imported > 0) parts.push(`تم استيراد ${data.imported} عميل`);
        if (data.skipped_duplicate > 0) parts.push(`${data.skipped_duplicate} مكرر`);
        if (data.skipped_blocked > 0) parts.push(`${data.skipped_blocked} محظور`);
        if (data.invalid > 0) parts.push(`${data.invalid} غير صالح`);
        toast.success(parts.join(' · ') || 'لا توجد جهات اتصال جديدة');
        loadData();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الاستيراد');
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Google sync ───────────────────────────────────────────────────

  async function connectGoogle() {
    try {
      const { data } = await api.get('/backoffice/contacts-sync/google/auth-url');
      if (data.success) {
        window.location.href = data.url;
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في ربط Google');
    }
  }

  function openDisconnect(provider) {
    const count = clients.filter((c) => c.source === provider).length;
    setDisconnectTarget({ provider, count });
  }

  async function confirmDisconnect(removeContacts) {
    if (!disconnectTarget) return;
    const { provider } = disconnectTarget;
    setDisconnecting(true);
    try {
      await api.delete(`/backoffice/contacts-sync/${provider}?remove_contacts=${removeContacts ? '1' : '0'}`);
      toast.success(provider === 'google' ? 'تم قطع اتصال Google' : 'تم قطع اتصال iCloud');
      setSyncStatus((s) => ({ ...s, [provider]: null }));
      setDisconnectTarget(null);
      loadData();
    } catch {
      toast.error('خطأ في قطع الاتصال');
    }
    setDisconnecting(false);
  }

  function syncGoogleNow() {
    // Fire-and-forget via the shared store so it survives page navigation.
    startSync('google');
  }

  // ── Apple sync ────────────────────────────────────────────────────

  async function connectApple(e) {
    e.preventDefault();
    if (!appleId.trim() || !appPassword.trim()) return;
    setConnectingApple(true);
    try {
      const { data } = await api.post('/backoffice/contacts-sync/apple', {
        apple_id: appleId.trim(),
        app_password: appPassword.trim(),
      });
      if (data.success) {
        toast.success('تم ربط iCloud Contacts بنجاح!');
        setSyncStatus((s) => ({ ...s, apple: data.provider }));
        setShowAppleModal(false);
        setAppleId('');
        setAppPassword('');
        loadData();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في ربط iCloud');
    }
    setConnectingApple(false);
  }


  function syncAppleNow() {
    // Fire-and-forget via the shared store so it survives page navigation.
    startSync('apple');
  }

  // ── Selection & bulk actions ──────────────────────────────────────

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(visibleIds) {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function runBulk(action) {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (action === 'delete' && !window.confirm(`حذف ${ids.length} عميل من القائمة؟`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/backoffice/allowed-clients/bulk', { ids, action });
      if (data.success) {
        const verb = action === 'delete' ? 'حذف' : action === 'block' ? 'حظر' : 'السماح لـ';
        toast.success(`تم ${verb} ${data.affected} عميل`);
        clearSelection();
        loadData();
      } else {
        toast.error(data.message || 'فشل الإجراء');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تنفيذ الإجراء');
    }
    setBulkBusy(false);
  }

  // ── Inline edit ───────────────────────────────────────────────────

  function startEdit(client) {
    setEditingId(client.id);
    setEditName(client.name || '');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  async function saveEdit(id) {
    try {
      const { data } = await api.put(`/backoffice/allowed-clients/${id}`, { name: editName.trim() || null });
      if (data.success) {
        setClients((prev) => prev.map((c) => (c.id === id ? data.client : c)));
        toast.success('تم تحديث الاسم');
        cancelEdit();
      } else {
        toast.error(data.message || 'فشل التحديث');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في التحديث');
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  function formatSyncTime(isoString) {
    if (!isoString) return null;
    const date = new Date(isoString);
    const diff = Math.floor((Date.now() - date) / 60000);
    if (diff < 2) return 'منذ لحظات';
    if (diff < 60) return `منذ ${diff} دقيقة`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `منذ ${hours} ساعة`;
    return date.toLocaleDateString('ar');
  }

  const filtered = clients.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (sourceFilter !== 'all' && (c.source || 'manual') !== sourceFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.phone && c.phone.includes(q))
    );
  });

  const activeCount = clients.filter((c) => c.status === 'active').length;
  const blockedCount = clients.filter((c) => c.status === 'blocked').length;

  // Sources that actually exist in the data → dynamic filter chips
  const sourceCounts = clients.reduce((acc, c) => {
    const s = c.source || 'manual';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const availableSources = Object.keys(SOURCE_FILTER_LABELS).filter((s) => sourceCounts[s]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // Select-all applies to the ENTIRE filtered set (across all pages)
  const filteredIds = filtered.map((c) => c.id);
  const selectedCount = selectedIds.size;
  const filteredSelectedCount = filteredIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = filteredSelectedCount > 0;

  if (loading) {
    return <div className="ca-page"><div className="ca-loading">جاري التحميل...</div></div>;
  }

  return (
    <div className="ca-page">
      <div className="ca-header">
        <h1>&#128274; إدارة الوصول</h1>
        <p className="ca-sub">تحكم بمن يمكنه رؤية صالونك والحجز فيه</p>
      </div>

      {/* Private Mode Toggle */}
      <div className="ca-toggle-card">
        <div className="ca-toggle-info">
          <h3>الوضع الخاص</h3>
          <p>عند التفعيل، فقط العملاء في قائمتك يمكنهم رؤية صالونك والحجز</p>
        </div>
        <label className="ca-switch">
          <input type="checkbox" checked={privateMode} onChange={togglePrivateMode} />
          <span className="ca-slider"></span>
        </label>
      </div>

      {privateMode && (
        <>
          {/* Contacts Sync — compact, first option */}
          <div className="ca-sync-compact">
            <div className="ca-sync-compact-head">
              <span className="ca-sync-compact-title">&#128260; مزامنة جهات الاتصال</span>
              <span className="ca-sync-compact-hint">تتحدث تلقائياً كل ساعة</span>
            </div>

            {/* Google row */}
            <div className={`ca-sync-item ${syncStatus.google ? 'connected' : ''}`}>
              <div className="ca-sync-item-icon ca-sync-google-logo">G</div>
              <div className="ca-sync-item-text">
                <div className="ca-sync-item-title">
                  Google Contacts
                  {syncStatus.google && <span className="ca-sync-badge">مرتبط</span>}
                </div>
                {syncingGoogle ? (
                  <div className="ca-sync-item-syncing">
                    <span className="ca-sync-mini-spinner" />
                    جاري مزامنة جهات الاتصال...
                  </div>
                ) : syncStatus.google ? (
                  <div className="ca-sync-item-meta">
                    {syncStatus.google.connected_email && <span>{syncStatus.google.connected_email}</span>}
                    {syncStatus.google.last_sync_count != null && <span> · {syncStatus.google.last_sync_count} جهة</span>}
                    {syncStatus.google.last_sync_at && <span> · {formatSyncTime(syncStatus.google.last_sync_at)}</span>}
                    {syncStatus.google.sync_error && <span className="ca-sync-item-err"> · ⚠️ خطأ</span>}
                  </div>
                ) : (
                  <div className="ca-sync-item-meta">استورد جهات اتصال Google وزامنها تلقائياً</div>
                )}
              </div>
              <div className="ca-sync-item-actions">
                {syncStatus.google ? (
                  <>
                    <button className="ca-icon-btn" onClick={syncGoogleNow} disabled={syncingGoogle} title="مزامنة الآن">
                      {syncingGoogle ? '…' : '\u21bb'}
                    </button>
                    <button className="ca-icon-btn ca-icon-btn-danger" onClick={() => openDisconnect('google')} title="قطع الاتصال">
                      &#10005;
                    </button>
                  </>
                ) : (
                  <button className="ca-btn-sm ca-btn-connect-google" onClick={connectGoogle}>
                    ربط
                  </button>
                )}
              </div>
            </div>

            {/* Apple row */}
            <div className={`ca-sync-item ${syncStatus.apple ? 'connected' : ''}`}>
              <div className="ca-sync-item-icon ca-sync-apple-logo">&#63743;</div>
              <div className="ca-sync-item-text">
                <div className="ca-sync-item-title">
                  iCloud Contacts
                  {syncStatus.apple && <span className="ca-sync-badge">مرتبط</span>}
                </div>
                {syncingApple ? (
                  <div className="ca-sync-item-syncing">
                    <span className="ca-sync-mini-spinner" />
                    جاري مزامنة جهات الاتصال...
                  </div>
                ) : syncStatus.apple ? (
                  <div className="ca-sync-item-meta">
                    {syncStatus.apple.connected_email && <span>{syncStatus.apple.connected_email}</span>}
                    {syncStatus.apple.last_sync_count != null && <span> · {syncStatus.apple.last_sync_count} جهة</span>}
                    {syncStatus.apple.last_sync_at && <span> · {formatSyncTime(syncStatus.apple.last_sync_at)}</span>}
                    {syncStatus.apple.sync_error && <span className="ca-sync-item-err"> · ⚠️ خطأ</span>}
                  </div>
                ) : (
                  <div className="ca-sync-item-meta">استورد جهات اتصال iPhone/iPad وزامنها تلقائياً</div>
                )}
              </div>
              <div className="ca-sync-item-actions">
                {syncStatus.apple ? (
                  <>
                    <button className="ca-icon-btn" onClick={syncAppleNow} disabled={syncingApple} title="مزامنة الآن">
                      {syncingApple ? '…' : '\u21bb'}
                    </button>
                    <button className="ca-icon-btn ca-icon-btn-danger" onClick={() => openDisconnect('apple')} title="قطع الاتصال">
                      &#10005;
                    </button>
                  </>
                ) : (
                  <button className="ca-btn-sm ca-btn-connect-apple" onClick={() => setShowAppleModal(true)}>
                    ربط
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Apple Connect Modal */}
          {showAppleModal && (
            <div className="ca-modal-overlay" onClick={() => setShowAppleModal(false)}>
              <div className="ca-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ca-modal-header">
                  <h3>&#63743; ربط iCloud Contacts</h3>
                  <button className="ca-modal-close" onClick={() => setShowAppleModal(false)}>&#10005;</button>
                </div>
                <div className="ca-modal-body">
                  <div className="ca-apple-steps">
                    <p className="ca-apple-step-title">📋 كيفية الحصول على كلمة مرور التطبيق:</p>
                    <ol>
                      <li>افتح <a href="https://appleid.apple.com" target="_blank" rel="noopener noreferrer" className="ca-apple-link">appleid.apple.com</a> في المتصفح</li>
                      <li>سجّل الدخول ← قسم <strong>الأمان</strong></li>
                      <li>انقر <strong>إنشاء كلمة مرور خاصة بالتطبيق</strong></li>
                      <li>أدخل اسماً (مثلاً: ادواري) ← انسخ كلمة المرور</li>
                    </ol>
                    <p className="ca-apple-note">⚠️ هذه كلمة مرور منفصلة عن كلمة مرور Apple ID — يمكن إلغاؤها في أي وقت</p>
                  </div>
                  <form onSubmit={connectApple} className="ca-apple-form">
                    <label>Apple ID (البريد الإلكتروني)</label>
                    <input
                      type="email"
                      placeholder="yourname@icloud.com"
                      value={appleId}
                      onChange={(e) => setAppleId(e.target.value)}
                      required
                      dir="ltr"
                    />
                    <label>كلمة مرور التطبيق (App-Specific Password)</label>
                    <input
                      type="password"
                      placeholder="xxxx-xxxx-xxxx-xxxx"
                      value={appPassword}
                      onChange={(e) => setAppPassword(e.target.value)}
                      required
                      dir="ltr"
                    />
                    <button
                      type="submit"
                      className="ca-btn ca-btn-submit"
                      disabled={connectingApple}
                    >
                      {connectingApple ? 'جاري التحقق...' : 'ربط iCloud'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* List header: title + import/add */}
          <div className="ca-list-header">
            <h3 className="ca-list-title">العملاء المسموح لهم <span className="ca-list-count">{clients.length}</span></h3>
            <div className="ca-list-header-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept=".vcf,.csv"
                style={{ display: 'none' }}
                onChange={handleFileImport}
              />
              <button
                className="ca-btn-sm ca-btn-import"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? 'جاري الاستيراد...' : '\u{1F4E5} استيراد'}
              </button>
              <button className="ca-btn-sm ca-btn-add" onClick={() => setShowAddForm(!showAddForm)}>
                &#10133; إضافة
              </button>
            </div>
          </div>

          {/* Add Form */}
          {showAddForm && (
            <form className="ca-add-form" onSubmit={addClient}>
              <input
                type="tel"
                placeholder="رقم الهاتف (مثال: 0501234567)"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                required
                dir="ltr"
              />
              <input
                type="text"
                placeholder="الاسم (اختياري)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <button type="submit" className="ca-btn ca-btn-submit">إضافة</button>
              <button type="button" className="ca-btn ca-btn-cancel" onClick={() => setShowAddForm(false)}>إلغاء</button>
            </form>
          )}

          {/* Compact filter bar: search + status segmented + source dropdown */}
          <div className="ca-filter-bar">
            <input
              type="text"
              className="search-input ca-filter-search"
              placeholder="بحث بالاسم أو الهاتف..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="ca-segmented">
              <button
                className={`ca-seg-btn ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                الكل {clients.length}
              </button>
              <button
                className={`ca-seg-btn ${statusFilter === 'active' ? 'active' : ''}`}
                onClick={() => setStatusFilter('active')}
              >
                نشط {activeCount}
              </button>
              <button
                className={`ca-seg-btn ${statusFilter === 'blocked' ? 'active' : ''}`}
                onClick={() => setStatusFilter('blocked')}
              >
                محظور {blockedCount}
              </button>
            </div>
            {availableSources.length > 1 && (
              <select
                className="ca-source-select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="all">كل المصادر ({clients.length})</option>
                {availableSources.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_FILTER_LABELS[s]} ({sourceCounts[s]})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div className="ca-bulk-bar">
              <div className="ca-bulk-info">
                <span className="ca-bulk-count">{selectedCount}</span> محدد
              </div>
              <div className="ca-bulk-actions">
                <button className="ca-bulk-btn ca-bulk-allow" onClick={() => runBulk('unblock')} disabled={bulkBusy}>
                  &#9989; السماح
                </button>
                <button className="ca-bulk-btn ca-bulk-block" onClick={() => runBulk('block')} disabled={bulkBusy}>
                  &#128683; حظر
                </button>
                <button className="ca-bulk-btn ca-bulk-delete" onClick={() => runBulk('delete')} disabled={bulkBusy}>
                  &#128465; حذف
                </button>
                <button className="ca-bulk-btn ca-bulk-clear" onClick={clearSelection} disabled={bulkBusy}>
                  إلغاء التحديد
                </button>
              </div>
            </div>
          )}

          {/* Client List */}
          {filtered.length === 0 ? (
            <div className="ca-empty">
              <span className="empty-icon">&#128100;</span>
              <p>{(search || statusFilter !== 'all' || sourceFilter !== 'all') ? 'لا توجد نتائج مطابقة للتصفية' : 'لا يوجد عملاء بعد. استورد جهات اتصال أو أضف عملاء يدوياً.'}</p>
            </div>
          ) : (
            <div className="ca-table-wrap">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th className="ca-check-col">
                      <input
                        type="checkbox"
                        className="ca-checkbox"
                        checked={allVisibleSelected}
                        ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                        onChange={() => toggleSelectAll(filteredIds)}
                        title="تحديد كل النتائج"
                      />
                    </th>
                    <th>الاسم</th>
                    <th>رقم الهاتف</th>
                    <th>المصدر</th>
                    <th>الحالة</th>
                    <th>تاريخ الإضافة</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((c) => {
                    const src = SOURCE_LABELS[c.source] || SOURCE_LABELS.manual;
                    const isSynced = c.source === 'google' || c.source === 'apple';
                    const isEditing = editingId === c.id;
                    const isSelected = selectedIds.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        className={`${c.status === 'blocked' ? 'row-blocked' : ''} ${isSelected ? 'row-selected' : ''}`}
                      >
                        <td className="ca-check-col">
                          <input
                            type="checkbox"
                            className="ca-checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(c.id)}
                          />
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="ca-inline-edit">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit(c.id);
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                                placeholder="الاسم"
                              />
                              <button className="ca-inline-save" onClick={() => saveEdit(c.id)} title="حفظ">&#10003;</button>
                              <button className="ca-inline-cancel" onClick={cancelEdit} title="إلغاء">&#10005;</button>
                            </div>
                          ) : (
                            <span className="ca-name-cell" onDoubleClick={() => startEdit(c)}>
                              {c.name || '—'}
                            </span>
                          )}
                        </td>
                        <td dir="ltr">{c.phone}</td>
                        <td>
                          {src.label ? (
                            <span className={`ca-source-badge ca-source-${c.source}`} title={isSynced ? 'مزامنة تلقائية' : src.label}>
                              {src.icon} {src.label}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          <span className={`ca-status-badge ca-status-${c.status}`}>
                            {c.status === 'active' ? 'نشط' : 'محظور'}
                          </span>
                        </td>
                        <td>{c.created_at ? new Date(c.created_at).toLocaleDateString('ar') : '—'}</td>
                        <td className="ca-actions-cell">
                          {!isEditing && (
                            <button className="ca-action-btn ca-action-edit ca-action-icon-only" onClick={() => startEdit(c)} title="تعديل الاسم">
                              <span className="ca-action-ico" aria-hidden="true">&#9998;</span>
                            </button>
                          )}
                          {c.status === 'active' ? (
                            <button className="ca-action-btn ca-action-block" onClick={() => blockClient(c.id)} title="حظر">
                              <span className="ca-action-ico" aria-hidden="true">&#8856;</span>
                              <span className="ca-action-label">حظر</span>
                            </button>
                          ) : (
                            <button className="ca-action-btn ca-action-unblock" onClick={() => unblockClient(c.id)} title="إلغاء الحظر">
                              <span className="ca-action-ico" aria-hidden="true">&#10003;</span>
                              <span className="ca-action-label">إلغاء الحظر</span>
                            </button>
                          )}
                          <button
                            className="ca-action-btn ca-action-delete"
                            onClick={() => deleteClient(c.id)}
                            title={isSynced ? `حذف (مؤقت — سيعود عند المزامنة)` : 'حذف'}
                          >
                            <span className="ca-action-ico" aria-hidden="true">&#128465;</span>
                            <span className="ca-action-label">حذف</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filtered.length > PAGE_SIZE && (
            <div className="ca-pagination">
              <button
                className="ca-page-btn"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                &#8594; السابق
              </button>
              <span className="ca-page-info">
                صفحة {currentPage} من {totalPages}
                <span className="ca-page-range">
                  ({pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} من {filtered.length})
                </span>
              </span>
              <button
                className="ca-page-btn"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                التالي &#8592;
              </button>
            </div>
          )}

          {/* First-sync loading overlay */}
          {firstSyncProvider && (
            <div className="ca-modal-overlay">
              <div className="ca-sync-loader" onClick={(e) => e.stopPropagation()}>
                <div className="ca-sync-spinner" />
                <div className="ca-sync-loader-title">
                  {firstSyncProvider === 'google'
                    ? 'جاري تحميل جهات الاتصال من Google...'
                    : 'جاري تحميل جهات الاتصال...'}
                </div>
                <div className="ca-sync-loader-sub">قد يستغرق هذا بضع ثوانٍ</div>
              </div>
            </div>
          )}

          {/* Disconnect modal */}
          {disconnectTarget && (
            <div className="ca-modal-overlay" onClick={() => !disconnecting && setDisconnectTarget(null)}>
              <div className="ca-modal ca-disconnect-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ca-modal-header">
                  <h3>قطع اتصال {disconnectTarget.provider === 'google' ? 'Google Contacts' : 'iCloud Contacts'}</h3>
                  <button className="ca-modal-close" onClick={() => setDisconnectTarget(null)} disabled={disconnecting}>&#10005;</button>
                </div>
                <div className="ca-modal-body">
                  <p className="ca-disconnect-q">
                    ماذا تريد أن تفعل بـ <strong>{disconnectTarget.count}</strong> جهة اتصال تم استيرادها من هذا المصدر؟
                  </p>
                  <div className="ca-disconnect-options">
                    <button
                      className="ca-btn ca-disconnect-keep"
                      onClick={() => confirmDisconnect(false)}
                      disabled={disconnecting}
                    >
                      <span className="ca-disconnect-opt-title">الاحتفاظ بها</span>
                      <span className="ca-disconnect-opt-desc">تبقى في القائمة كعملاء يدويين ولن تُزامن بعد الآن</span>
                    </button>
                    <button
                      className="ca-btn ca-disconnect-remove"
                      onClick={() => confirmDisconnect(true)}
                      disabled={disconnecting}
                    >
                      <span className="ca-disconnect-opt-title">حذفها من القائمة</span>
                      <span className="ca-disconnect-opt-desc">إزالة كل جهات الاتصال المستوردة من هذا المصدر</span>
                    </button>
                  </div>
                  <button className="ca-btn ca-btn-cancel ca-disconnect-cancel" onClick={() => setDisconnectTarget(null)} disabled={disconnecting}>
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
